import { useRef } from "react";
import * as _ from "lodash";
import { BarConfig, Datum } from "@ant-design/charts";
import { useDeepCompareEffect, useDeepCompareMemo } from "use-deep-compare";

import { BarData, ChartSeriesType, DataPointsMeta } from "@ctra/api";
import { hslToHex } from "@ctra/utils";
import { Enterprise as Content, useTranslation } from "@ctra/i18n";

import { useChartContext, useSeriesColor } from "../../../providers";
import { useCommonConfig } from "../../../hooks";

import { Processor } from "./processing/Factory";
import { SingleEntityBarChartProcessor } from "./processing/Individual";
import { MultiEntityBarChartProcessor } from "./processing/Combined";

import "../Tooltip.css";

const {
  kpi: { displayName }
} = Content;

/**
 * Hook that merges bar config with the common config
 * The common config coming from the main app overrides the preset config
 * @returns
 */
export const useBarConfig = (): { config: BarConfig } => {
  const { commonConfig } = useCommonConfig();
  const chartContext = useChartContext<BarData>();
  const { t } = useTranslation();

  const {
    paletteType,
    api: { getColor, setPaletteType }
  } = useSeriesColor();

  const {
    values: { entities, series: s, data: d, meta: m },
    api: { setConsolidatedSeries, setConsolidatedData }
  } = chartContext;

  /**
   * Create a ref for holding the chart config utility
   * @type {React.MutableRefObject<Processor>}
   */
  const processor = useRef<SingleEntityBarChartProcessor | MultiEntityBarChartProcessor>(
    Processor.create(entities)
  );

  /**
   * Recreate the processor when the entities change
   */
  useDeepCompareEffect(() => {
    processor.current = Processor.create(entities);
  }, [entities]);

  /**
   * Tell if the chart has overlays.
   * Multi-type and multi-farm is considered a factor, not the number of entities.
   * This way we can consider overlays "just grouped bars".
   * @type {boolean}
   */
  const hasOverlays = useDeepCompareMemo(() => {
    const seriesTypes = _.map(m, "seriesType");

    /**
     * Multi-farm charts get a special treatment.
     * We will compare farms so the color will be determined by the series type.
     * @type {boolean}
     */
    const isMultiFarm = _.size(_.uniq(_.flatMap(m, "farmIds"))) > 1;

    /**
     * Check if there are multiple series types
     * @type {boolean}
     */
    const isMultiType = _.size(_.compact(seriesTypes)) > 1;

    return isMultiType && !isMultiFarm;
  }, [m]);

  /**
   * Update the palette type based on the overlay and the metadata
   */
  useDeepCompareEffect(() => {
    if (hasOverlays && paletteType !== ChartSeriesType.overlay) {
      setPaletteType(ChartSeriesType.overlay);
    } else {
      const [seriesType] = _.uniq(_.compact(_.map(m, "seriesType")));

      setPaletteType(seriesType);
    }
  }, [hasOverlays, m]);

  /**
   * Get the series from the processor
   * @type {SeriesType}
   */
  const series = useDeepCompareMemo(
    () => processor.current.getSeries(m, s),
    [m, s, processor.current.identity]
  );

  /**
   * Update the values for use in other context depending on the data
   */
  useDeepCompareEffect(() => {
    setConsolidatedSeries(series);
  }, [series]);

  /**
   * Get the data from the processor
   * @type {Array<{x: number, y: string, seriesField: string, context?: PointContext}>}
   */
  const data = useDeepCompareMemo(
    () => processor.current.getData(d, m, s),
    [d, m, s, processor.current.identity]
  );

  /**
   * Update the data for use in other context depending on the data
   */
  useDeepCompareEffect(() => {
    setConsolidatedData(data);
  }, [data]);

  /**
   * Count the number of items in the legend
   * @type {number}
   */
  const legendSize = _.size(series);

  const [main] = entities;
  const { axis, yAxis } = _.get(m, [main.id], {} as DataPointsMeta);

  /**
   * Bar chart config
   * @type {{yAxis: {line: {style: {stroke: string, lineWidth: number}}, label: {style: {fontSize: number, stroke: string, lineWidth: number}}, tickLine: {length: number, style: {stroke: string, lineWidth: number}}}, xAxis: {min: number, max: number, line: {style: {stroke: string, lineWidth: number}}, label: {offset: number}, title: {style: {fontSize: number, fontWeight: string}, text: string | undefined}, nice: boolean, tickLine: {length: number, style: {stroke: string, lineWidth: number}}}, xField: string, color: ({seriesField}: {seriesField: any}) => string, data: any[], yField: string, tooltip: {shared: boolean, customContent: (_title, content) => HTMLElement, showTitle: boolean}, seriesField: string, intervalPadding: number}}
   */
  const config: BarConfig = useDeepCompareMemo(() => {
    return {
      isGroup: processor.current.mayGroup(m),
      color: ({ seriesField }: Datum) => {
        return hslToHex(getColor(void 0, seriesField));
      },
      /**
       * turn off the legends if there is only one series
       */
      legend:
        legendSize > 1
          ? {
              ...commonConfig.legend,
              itemName: {
                formatter: (seriesField: string) => {
                  const metricName = _.get(_.find(m, ["variantId", seriesField]), ["title"]);

                  return _.get(
                    series,
                    [seriesField],
                    metricName
                      ? t<string>(displayName(metricName), {
                          case: null,
                          variant: null,
                          makeDefaultValue: true
                        })
                      : seriesField
                  );
                }
              }
            }
          : false,
      data,
      seriesField: "seriesField",
      tooltip: {
        shared: true, // legendSize <= 3,
        containerTpl: `
          <div class="g2-tooltip g2-barChart-tooltip">
            <ul class="g2-tooltip-list"></ul>
          </div>
        `,
        showTitle: false,
        formatter: ({ x, seriesField }: Datum) => {
          const metricName = _.get(_.find(m, ["variantId", seriesField]), ["title"]);

          return {
            name: _.get(
              series,
              [seriesField],
              metricName
                ? t<string>(displayName(metricName), {
                    case: null,
                    variant: null,
                    makeDefaultValue: true
                  })
                : seriesField
            ),
            value: _.round(x, 2) // TODO round to data descriptor
          };
        }
      },
      xAxis: {
        label: {
          formatter: (value) => {
            const metricName = _.get(_.find(m, ["variantId", value]), ["title"]);

            return _.get(
              series,
              [value],
              metricName
                ? t<string>(displayName(metricName), {
                    case: null,
                    variant: null,
                    makeDefaultValue: true
                  })
                : value
            );
          },
          offset: 10,
          autoRotate: true,
          autoHide: false,
          autoEllipsis: true
        },
        line: {
          style: {
            lineWidth: 0.5,
            stroke: "#000000"
          }
        },
        tickLine: {
          style: {
            lineWidth: 1,
            stroke: "#000000"
          }
        }
      },
      yAxis: {
        title: hasOverlays
          ? void 0
          : {
              style: {
                fontSize: 14,
                fontWeight: "bolder"
              },
              text: yAxis?.unit ? `${axis?.x.title}(${yAxis.unit})` : axis?.x.title
            },
        label: {
          style: {
            fontSize: 11,
            lineWidth: 0.5,
            stroke: "#000000"
          }
        },
        line: {
          style: {
            lineWidth: 0.5,
            stroke: "#000000"
          }
        },
        tickLine: {
          length: 5,
          style: {
            lineWidth: 1,
            stroke: "#000000"
          }
        }
      },
      xField: "y",
      yField: "x"
    };
  }, [data, m, series, legendSize, commonConfig]);

  return useDeepCompareMemo(
    () => ({
      config: _.mergeWith({}, commonConfig, config, (objValue, srcValue) => {
        if (_.isFunction(objValue) && _.isFunction(srcValue)) {
          return _.over([objValue, srcValue]);
        }
      })
    }),
    [commonConfig, config]
  );
};
